/**
 * Streamdown Animation
 *
 * Shared animation timing for Streamdown-rendered markdown.
 *
 * Primary responsibilities:
 * - Keep Streamdown animation duration and easing consistent
 * - Track streaming animation state per markdown block
 * - Avoid duplicating animation settings across chat surfaces
 *
 * Dependencies:
 * - streamdown
 */

import { createElement, memo, useMemo } from 'react'
import {
  Block,
  createAnimatePlugin,
  type AnimateOptions,
  type BlockProps,
  type StreamdownProps
} from 'streamdown'

export const streamdownAnimation = {
  animation: 'blurIn',
  duration: 250,
  easing: 'ease-out'
} satisfies AnimateOptions

/** Finished markdown — no remend, no caret/blur streaming behavior. */
export const streamdownStaticProps = {
  mode: 'static',
  parseIncompleteMarkdown: false,
  isAnimating: false
} satisfies Pick<
  StreamdownProps,
  'mode' | 'parseIncompleteMarkdown' | 'isAnimating'
>

/** Live assistant stream props; omit animation when not streaming. */
export function getStreamdownStreamProps(isStreaming: boolean): Pick<
  StreamdownProps,
  'mode' | 'parseIncompleteMarkdown' | 'isAnimating' | 'animated'
> {
  if (!isStreaming) {
    return streamdownStaticProps
  }

  return {
    mode: 'streaming',
    parseIncompleteMarkdown: true,
    isAnimating: true,
    animated: streamdownAnimation
  }
}

export const StreamdownAnimateOnceBlock = memo(function StreamdownAnimateOnceBlock({
  animatePlugin,
  rehypePlugins,
  ...props
}: BlockProps) {
  const blockAnimatePlugin = useMemo(
    () => createAnimatePlugin(streamdownAnimation),
    []
  )
  const blockRehypePlugins = useMemo(() => {
    if (!animatePlugin || !rehypePlugins) {
      return rehypePlugins
    }

    return rehypePlugins.map((plugin) =>
      plugin === animatePlugin.rehypePlugin ? blockAnimatePlugin.rehypePlugin : plugin
    )
  }, [animatePlugin, blockAnimatePlugin, rehypePlugins])

  return createElement(Block, {
    ...props,
    animatePlugin: animatePlugin ? blockAnimatePlugin : null,
    rehypePlugins: blockRehypePlugins
  })
})
